import { Fragment, ReactNode } from 'react';
import { Box } from '@mui/material';

// 색상 매핑 (일반적인 색상 이름을 hex로 변환)
const colorMap: Record<string, string> = {
  red: '#ff0000',
  blue: '#0088ff',
  green: '#00ff00',
  orange: '#ffa500',
  purple: '#800080',
  yellow: '#ffff00',
  black: '#000000',
  gray: '#808080',
  grey: '#808080',
};

interface ParseNode {
  type: 'text' | 'bold' | 'color' | 'break';
  content?: string | ParseNode[];
  color?: string;
  id: string;
}

let idCounter = 0;
const generateId = () => `node-${Date.now()}-${++idCounter}`;

/**
 * 중첩 태그를 지원하는 텍스트 파서 (재귀적)
 * <b>볼드</b>, <c=red>색상</c>, <b><c=red>중첩</c></b> 형식 지원
 */
export function parseFormattedText(text: string): ParseNode[] {
  if (!text) return [];

  // Zero-width space 제거 (사용자 입력 실수 방지)
  text = text.replace(/\u200b/g, '');

  const nodes: ParseNode[] = [];

  // <b> 태그 처리
  const processBoldTag = (input: string): { nodes: ParseNode[]; remaining: string; processed: boolean } => {
    const result: ParseNode[] = [];
    let remaining = input;
    let processed = false;

    const boldIndex = remaining.indexOf('<b>');
    if (boldIndex !== -1) {
      // 태그 이전의 텍스트
      if (boldIndex > 0) {
        const plainText = remaining.substring(0, boldIndex);
        if (plainText) {
          result.push({ type: 'text', content: plainText, id: generateId() });
        }
      }

      // 닫는 태그 찾기
      let depth = 1;
      let j = boldIndex + 3;
      let contentEnd = -1;

      while (j < remaining.length && depth > 0) {
        if (remaining.substring(j, j + 3) === '<b>') {
          depth++;
          j += 3;
        } else if (remaining.substring(j, j + 4) === '</b>') {
          depth--;
          if (depth === 0) {
            contentEnd = j;
            break;
          }
          j += 4;
        } else {
          j++;
        }
      }

      if (contentEnd !== -1) {
        const content = remaining.substring(boldIndex + 3, contentEnd);
        // 내용을 재귀적으로 파싱 (중첩 태그 처리)
        const innerNodes = parseFormattedText(content);
        result.push({
          type: 'bold',
          content: innerNodes.length > 0 ? innerNodes : content,
          id: generateId(),
        });
        remaining = remaining.substring(contentEnd + 4);
        processed = true;
      }
    }

    return { nodes: result, remaining, processed };
  };

  // <c=색상> 태그 처리
  const processColorTag = (input: string): { nodes: ParseNode[]; remaining: string; processed: boolean } => {
    const result: ParseNode[] = [];
    let remaining = input;
    let processed = false;

    const colorMatch = remaining.match(/<c=([^>]+)>/);
    if (colorMatch && colorMatch.index !== undefined) {
      const matchIndex = colorMatch.index;
      const color = colorMatch[1];
      const tagLength = colorMatch[0].length;

      // 태그 이전의 텍스트
      if (matchIndex > 0) {
        const plainText = remaining.substring(0, matchIndex);
        if (plainText) {
          result.push({ type: 'text', content: plainText, id: generateId() });
        }
      }

      // 닫는 태그 찾기
      let depth = 1;
      let j = matchIndex + tagLength;
      let contentEnd = -1;

      while (j < remaining.length && depth > 0) {
        const nextColorMatch = remaining.substring(j).match(/^<c=([^>]+)>/);
        if (nextColorMatch) {
          depth++;
          j += nextColorMatch[0].length;
        } else if (remaining.substring(j, j + 4) === '</c>') {
          depth--;
          if (depth === 0) {
            contentEnd = j;
            break;
          }
          j += 4;
        } else {
          j++;
        }
      }

      if (contentEnd !== -1) {
        const content = remaining.substring(matchIndex + tagLength, contentEnd);
        // 내용을 재귀적으로 파싱 (중첩 태그 처리)
        const innerNodes = parseFormattedText(content);
        result.push({
          type: 'color',
          content: innerNodes.length > 0 ? innerNodes : content,
          color,
          id: generateId(),
        });
        remaining = remaining.substring(contentEnd + 4);
        processed = true;
      }
    }

    return { nodes: result, remaining, processed };
  };

  // <br> 태그 처리
  const processBreakTag = (input: string): { nodes: ParseNode[]; remaining: string; processed: boolean } => {
    const result: ParseNode[] = [];
    let remaining = input;
    let processed = false;

    const breakIndex = remaining.indexOf('\n');
    if (breakIndex !== -1) {
      // 줄바꿈 이전의 텍스트
      if (breakIndex > 0) {
        const plainText = remaining.substring(0, breakIndex);
        if (plainText) {
          result.push({ type: 'text', content: plainText, id: generateId() });
        }
      }

      result.push({ type: 'break', id: generateId() });
      remaining = remaining.substring(breakIndex + 1);
      processed = true;
    }

    return { nodes: result, remaining, processed };
  };

  // 태그 파싱 반복
  let remaining = text;
  let lastProcessed = true;

  while (lastProcessed) {
    lastProcessed = false;

    // 볼드 태그, 색상 태그, 줄바꿈 중 먼저 나오는 것을 처리
    const boldIndex = remaining.indexOf('<b>');
    const colorMatch = remaining.match(/<c=([^>]+)>/);
    const colorIndex = colorMatch && colorMatch.index !== undefined ? colorMatch.index : -1;
    const breakIndex = remaining.indexOf('\n');

    let action = 'none';
    let minIndex = remaining.length;

    if (boldIndex !== -1 && boldIndex < minIndex) {
      minIndex = boldIndex;
      action = 'bold';
    }
    if (colorIndex !== -1 && colorIndex < minIndex) {
      minIndex = colorIndex;
      action = 'color';
    }
    if (breakIndex !== -1 && breakIndex < minIndex) {
      minIndex = breakIndex;
      action = 'break';
    }

    if (action === 'bold') {
      const result = processBoldTag(remaining);
      nodes.push(...result.nodes);
      remaining = result.remaining;
      if (result.processed) lastProcessed = true;
    } else if (action === 'color') {
      const result = processColorTag(remaining);
      nodes.push(...result.nodes);
      remaining = result.remaining;
      if (result.processed) lastProcessed = true;
    } else if (action === 'break') {
      const result = processBreakTag(remaining);
      nodes.push(...result.nodes);
      remaining = result.remaining;
      if (result.processed) lastProcessed = true;
    }
  }

  // 남은 텍스트
  if (remaining.trim()) {
    nodes.push({ type: 'text', content: remaining, id: generateId() });
  }

  // 태그가 없으면 전체를 일반 텍스트로
  if (nodes.length === 0 && text.trim()) {
    return [{ type: 'text', content: text, id: generateId() }];
  }

  return nodes;
}

/**
 * 파싱된 노드를 React 컴포넌트로 렌더링
 */
export function renderFormattedText(nodes: ParseNode[]): ReactNode[] {
  return nodes.map(node => {
    if (node.type === 'text') {
      return <Fragment key={node.id}>{node.content as string}</Fragment>;
    }

    if (node.type === 'bold') {
      const content = Array.isArray(node.content) ? renderFormattedText(node.content) : node.content;
      return (
        <Box key={node.id} component="span" sx={{ fontWeight: 700 }}>
          {content}
        </Box>
      );
    }

    if (node.type === 'color') {
      const colorValue = node.color ? colorMap[node.color.toLowerCase()] || node.color : '#000000';
      const content = Array.isArray(node.content) ? renderFormattedText(node.content) : node.content;
      return (
        <Box key={node.id} component="span" sx={{ color: colorValue }}>
          {content}
        </Box>
      );
    }

    if (node.type === 'break') {
      return (
        <Fragment key={node.id}>
          <br />
          <br />
        </Fragment>
      );
    }

    return null;
  });
}
